import React from "react";
import values from "lodash/values";
import pick from "lodash/pickBy";
import moment from "moment";
import { Link } from "react-router";
import { RaisedButton } from "material-ui";
import { Table, Tr, Td, Sort } from "reactable";
import { colors } from "material-ui/styles";
import assignWith from "lodash/assignWith";

import { formatPrice, clientSideDownload, makeCsv } from "../util";

import connectToStores from "alt-utils/lib/connectToStores";
import GodActions from "../actions/GodActions";
import GodStore from "../stores/GodStore";
import UserStore from "../stores/UserStore";

import WorkingIndicator from "./partials/working-indicator.jsx";

class GodRevenue extends React.Component {
  constructor(props) {
    super(props);
    this.downloadCsv = this.downloadCsv.bind(this);
    this._makeRowData = this._makeRowData.bind(this);
    this._makeRowForRevenue = this._makeRowForRevenue.bind(this);
  }

  static getStores() {
    return [UserStore, GodStore];
  }

  static getPropsFromStores() {
    return {
      revenue: GodStore.getState().revenue,
      condensedUsers: UserStore.getState().condensedUsers
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  componentDidMount() {
    GodActions.fetchRevenue.defer();
  }

  getStyles() {
    return {};
  }

  downloadCsv() {
    clientSideDownload(
      `eventive-organizations-${new Date().toISOString()}.csv`,
      "text/csv",
      makeCsv(
        this._makeRowData().map(revenue => {
          const user = this.props.condensedUsers.find(
            user => user.id === revenue.organization
          ) || {
            organization: revenue.organization || "TOTAL",
            currency: "usd"
          };
          return {
            "Organization ID": user.id,
            Organization: user.organization,
            "Organization LTV": formatPrice(
              revenue.sigma.platform_fees.net + revenue.sigma.invoices.total,
              { currency: "usd" }
            ),
            "Organization 1y": formatPrice(
              revenue.sigma.platform_fees_1y.net +
                revenue.sigma.invoices_1y.total,
              { currency: "usd" }
            ),
            "Platform fees": formatPrice(revenue.sigma.platform_fees.net, {
              currency: "usd"
            }),
            "Platform fees 1y": formatPrice(
              revenue.sigma.platform_fees_1y.net,
              {
                currency: "usd"
              }
            ),
            "Payments net": formatPrice(revenue.sigma.payments.net, {
              currency: "usd"
            }),
            "Invoices total": formatPrice(revenue.sigma.invoices.total, {
              currency: "usd"
            }),
            "Invoices 1y": formatPrice(revenue.sigma.invoices_1y.total, {
              currency: "usd"
            })
          };
        })
      )
    );
  }

  _makeRowData() {
    const totalRow = this.props.revenue.reduce(
      (total, r) => {
        return {
          gross_volume: total.gross_volume + r.gross_volume,
          net_volume: total.net_volume + r.net_volume,
          sigma: {
            platform_fees: {
              net: total.sigma.platform_fees.net + r.sigma.platform_fees.net
            },
            payments: {
              total: total.sigma.payments.total + r.sigma.payments.total,
              net: total.sigma.payments.net + r.sigma.payments.net
            },
            platform_fees_1y: {
              net:
                total.sigma.platform_fees_1y.net + r.sigma.platform_fees_1y.net
            },
            payments_1y: {
              total: total.sigma.payments_1y.total + r.sigma.payments_1y.total,
              net: total.sigma.payments_1y.net + r.sigma.payments_1y.net
            },
            invoices: {
              total: total.sigma.invoices.total + r.sigma.invoices.total
            },
            invoices_1y: {
              total: total.sigma.invoices_1y.total + r.sigma.invoices_1y.total
            }
          }
        };
      },
      {
        gross_volume: 0,
        net_volume: 0,
        sigma: {
          platform_fees: { net: 0 },
          payments: { total: 0, net: 0 },
          platform_fees_1y: { net: 0 },
          payments_1y: { total: 0, net: 0 },
          invoices: { total: 0 },
          invoices_1y: { total: 0 }
        }
      }
    );
    return [totalRow].concat(this.props.revenue);
  }

  _makeRowForRevenue(revenue) {
    const user = this.props.condensedUsers.find(
        user => user.id === revenue.organization
      ) || {
        organization: revenue.organization || "TOTAL",
        currency: "usd"
      },
      isTotal = user.organization === "TOTAL";

    return (
      <Tr style={isTotal ? { fontWeight: "bold" } : {}}>
        <Td column="Organization" value={user.organization}>
          {isTotal ? (
            "TOTAL"
          ) : (
            <a
              href={`/organization_health?_god_become_user=${user.id}`}
              target="_blank"
            >
              {user.organization}
            </a>
          )}
        </Td>
        <Td
          column="Organization LTV"
          value={
            revenue.sigma.platform_fees.net + revenue.sigma.invoices.total || 0
          }
        >
          {formatPrice(
            revenue.sigma.platform_fees.net + revenue.sigma.invoices.total,
            { currency: "usd" }
          )}
        </Td>
        <Td
          column="Organization LTV 1y"
          value={
            revenue.sigma.platform_fees_1y.net +
              revenue.sigma.invoices_1y.total || 0
          }
        >
          {formatPrice(
            revenue.sigma.platform_fees_1y.net +
              revenue.sigma.invoices_1y.total,
            { currency: "usd" }
          )}
        </Td>
        <Td column="Fees" value={revenue.sigma.platform_fees.net || 0}>
          {formatPrice(revenue.sigma.platform_fees.net, { currency: "usd" })}
        </Td>
        <Td column="Fees 1y" value={revenue.sigma.platform_fees_1y.net || 0}>
          {formatPrice(revenue.sigma.platform_fees_1y.net, { currency: "usd" })}
        </Td>
        <Td column="Invoices" value={revenue.sigma.invoices.total || 0}>
          {formatPrice(revenue.sigma.invoices.total, { currency: "usd" })}
        </Td>
        <Td column="Invoices 1y" value={revenue.sigma.invoices_1y.total || 0}>
          {formatPrice(revenue.sigma.invoices_1y.total, { currency: "usd" })}
        </Td>
      </Tr>
    );
  }

  render() {
    let styles = this.getStyles();

    if (!this.props.revenue || !this.props.condensedUsers) {
      return <WorkingIndicator fullPage={true} />;
    }

    const rows = this._makeRowData().map(this._makeRowForRevenue);

    return (
      <div>
        <h1>God Revenue</h1>
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <RaisedButton
            onClick={() => this.downloadCsv()}
            label={`Download all data (.CSV)`}
          />
        </div>
        <Table
          className="bordered striped responsive-table"
          sortable={[
            "Organization",
            "Organization LTV",
            "Organization LTV 1y",
            "Fees",
            "Fees 1y",
            "Invoices",
            "Invoices 1y"
          ]}
          defaultSort={{ column: "Organization LTV 1y", direction: "desc" }}
          filterable={["Organization"]}
          filterPlaceholder="Type to filter..."
        >
          {rows}
        </Table>
        <p>
          <i>
            Sigma data last fetched{" "}
            {moment(
              this.props.revenue[0] ? this.props.revenue[0].sigma.updated_at : 0
            ).format("lll")}
            .
          </i>
        </p>
      </div>
    );
  }
}

export default connectToStores(GodRevenue);



// WEBPACK FOOTER //
// ./src/app/components/god-revenue.jsx